import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import * as stylex from "@stylexjs/stylex";
import { createFileRoute, redirect } from "@tanstack/react-router";
import AuditActions from "@/components/audit/AuditActions";
import AuditBrief from "@/components/audit/AuditBrief";
import AuditHeader, { AuditMoreMenu } from "@/components/audit/AuditHeader";
import AuditProposals from "@/components/audit/AuditProposals";
import PageHeader from "@/components/shell/PageHeader";
import EmptyState from "@/components/ui/EmptyState";
import { Page, PageBody, Section } from "@/components/ui/Page";
import { LoadingRegion, Skeleton, TableSkeleton } from "@/components/ui/Skeleton";
import { chooseAuditAccount } from "@/lib/audit-account";
import { auditSearchSchema } from "@/lib/search-params";
import { trpc } from "@/lib/trpc";
import { useWindowTitleDetail } from "@/lib/window-title";
import { workspaceModuleRedirect } from "@/lib/workspace-module-access";
import "@/design/tokens.stylex";
import { space } from "@/design/tokens.stylex";

// One reading width for the header, the brief and the tables, and room
// between the three blocks so the page reads as three.
const styles = {
  page: {
    ks0D6T: "xho4efa",
    $$css: true
  },
  body: {
    kOIVth: "xbbh9ds",
    $$css: true
  }
};

// Audit (chat-first ticket 10, ADR 0019): one page per Google account for a
// quick check: the latest brief's three things to do with the full Report a
// click away, the Actions that went live, and the open Proposals read-only. The conversation, approving and
// dismissing happen in the person's chat. The header names the account, with
// a picker only when the Company has several. Viewers get no Audit route; the
// server rejects them too. The Report and Proposal pages live outside this
// layout (audit_.reports, audit_.proposals).
export const Route = createFileRoute("/_authenticated/$orgSlug/audit")({
  validateSearch: auditSearchSchema,
  beforeLoad: async ({
    params,
    context
  }) => {
    const viewer = await context.queryClient.ensureQueryData(trpc.organization.viewer.queryOptions());
    const target = workspaceModuleRedirect(viewer, params.orgSlug);
    if (target) throw redirect(target);
  },
  loader: ({
    params,
    context
  }) => context.queryClient.ensureQueryData(trpc.audit.accounts.queryOptions({
    orgSlug: params.orgSlug
  })),
  component: AuditPage
});
function AuditPage() {
  const {
    orgSlug
  } = Route.useParams();
  const search = Route.useSearch();
  const {
    data: accounts
  } = useSuspenseQuery(trpc.audit.accounts.queryOptions({
    orgSlug
  }));
  // The link's account, else the one last chosen here, else the first.
  const account = chooseAuditAccount(orgSlug, accounts, search.account);
  useWindowTitleDetail(accounts.length > 1 ? account?.name : undefined);
  if (!account) {
    return <Page>
        <PageHeader title="Audit" actions={<AuditMoreMenu />} />
        <PageBody>
          <EmptyState title="No Google account is assigned to this Company.">
            A super admin or Company manager assigns one in Settings › Companies.
          </EmptyState>
        </PageBody>
      </Page>;
  }
  return <Page xstyle={styles.page}>
      <AuditHeader orgSlug={orgSlug} accounts={accounts} account={account} />
      <PageBody xstyle={styles.body}>
        <AccountAudit orgSlug={orgSlug} accountId={account.accountId} />
      </PageBody>
    </Page>;
}
function AccountAudit({
  orgSlug,
  accountId
}: {
  orgSlug: string;
  accountId: string;
}) {
  const input = {
    orgSlug,
    accountId
  };
  const reports = useQuery(trpc.audit.latestReports.queryOptions(input));
  // The runner settles rows between visits; keep the lists fresh while open.
  const proposals = useQuery({
    ...trpc.audit.proposals.queryOptions(input),
    refetchInterval: 30_000
  });
  const actions = useQuery({
    ...trpc.audit.actions.queryOptions(input),
    refetchInterval: 30_000
  });
  return <>
      {reports.data ? <AuditBrief orgSlug={orgSlug} reports={reports.data} /> : <LoadingRegion label="Loading the latest brief">
          <Skeleton width="60%" height={160} />
        </LoadingRegion>}
      <Section title="Actions" aria-label="Actions">
        {actions.data ? <AuditActions orgSlug={orgSlug} actions={actions.data.actions} /> : <TableSkeleton label="Loading Actions" rows={4} columns={5} />}
      </Section>
      {proposals.data ? <AuditProposals orgSlug={orgSlug} proposals={proposals.data.proposals} /> : <TableSkeleton label="Loading Proposals" rows={3} columns={1} />}
    </>;
}